import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Plus,
  FileText,
  TrendingUp,
  TrendingDown,
  Search,
  AlertTriangle,
  Edit3,
} from "lucide-react";
import { usePortfolioStore, Holding } from "../store/portfolioStore";
import { useTradingHistoryStore } from "../store/tradingHistoryStore";
import { useBrokerStore } from "../store/brokerStore";
import { useConfigStore } from "../store/configStore";
import BrokerLogo from "./BrokerLogo";
import { FORM_CONSTANTS } from "../constants/formConstants";
import { formatDate } from "../lib/utils";
import { apiFetch } from "../lib/api";
import Button, { buttonClass } from "./ui/Button";
import Select from "./ui/Select";
import FundSearchModal from "./FundSearchModal";
import toast from "react-hot-toast";

interface PrefillData {
  symbol?: string;
  name?: string;
  portfolioId?: string;
  port_api_type?: "us_stock" | "th_stock" | "th_fund" | "us_fund" | "gold";
  sector?: string;
  country?: string;
  currency?: "USD" | "THB";
  action?: "BUY" | "SELL";
  maxShares?: number;
  currentPrice?: number;
  purchasePrice?: number;
  purchaseCurrency?: string;
}

interface AddTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  prefillData?: PrefillData;
  editingHolding?: Holding;
}

const AddTransactionModal: React.FC<AddTransactionModalProps> = ({
  isOpen,
  onClose,
  prefillData,
  editingHolding,
}) => {
  const {
    customPortfolios,
    loadCustomPortfolios,
    exchangeRate,
    sectors,
    countries,
    loadSectors,
    loadCountries,
    openCustomPortfolioModal,
    sellHolding,
    convertPrice,
    updateHolding,
    removeHoldingFromPortfolio,
    assignHoldingToPortfolio,
    loadPortfolio,
    loadLatestPrices,
    portfolio,
    portfolioHoldings,
  } = usePortfolioStore();
  const { addTradingRecord, loadTradingHistory, loadPnLSummary } =
    useTradingHistoryStore();
  const { brokers, loadBrokers } = useBrokerStore();
  const { getConfigValue } = useConfigStore();

  const [action, setAction] = useState<"BUY" | "SELL">("BUY");
  const [formData, setFormData] = useState({
    symbol: "",
    portfolioId: "",
    port_api_type: "us_stock" as
      | "us_stock"
      | "th_stock"
      | "th_fund"
      | "us_fund"
      | "gold",
    shares: "",
    price: "",
    currency: "USD" as "USD" | "THB",
    fee: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
    proj_id: "",
    sector: "",
    country: "",
    broker: "",
  });
  const [loading, setLoading] = useState(false);
  const [fetchingFundData, setFetchingFundData] = useState(false);
  const [showFundSearchModal, setShowFundSearchModal] = useState(false);

  // Symbol combobox
  const [symbolQuery, setSymbolQuery] = useState("");
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const [selectedHoldingShares, setSelectedHoldingShares] = useState<
    number | null
  >(null);
  const symbolInputRef = useRef<HTMLInputElement>(null);
  const symbolDropdownRef = useRef<HTMLDivElement>(null);

  // PDF import
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [pdfPassword, setPdfPassword] = useState("");
  const [pdfParsing, setPdfParsing] = useState(false);

  const isEditMode = !!editingHolding;

  // Group holdings by portfolio for the dropdown
  const groupedSymbols = React.useMemo(() => {
    const q = symbolQuery.toUpperCase().trim();
    const groups: {
      portfolio: { id: number; name: string; icon: string };
      holdings: typeof portfolio;
    }[] = [];
    const seenSymbol = new Set<string>();

    for (const p of customPortfolios) {
      const holdings = (portfolioHoldings[p.id] || []).filter((h) => {
        if (seenSymbol.has(h.symbol)) return false;
        if (q && !h.symbol.includes(q) && !h.name?.toUpperCase().includes(q))
          return false;
        seenSymbol.add(h.symbol);
        return true;
      });
      if (holdings.length > 0) {
        groups.push({
          portfolio: { id: p.id, name: p.name, icon: p.icon },
          holdings,
        });
      }
    }

    // Holdings not in any known portfolio (fallback)
    const ungrouped = portfolio.filter((h) => {
      if (seenSymbol.has(h.symbol)) return false;
      if (q && !h.symbol.includes(q) && !h.name?.toUpperCase().includes(q))
        return false;
      seenSymbol.add(h.symbol);
      return true;
    });
    if (ungrouped.length > 0) {
      groups.push({
        portfolio: { id: -1, name: "Other", icon: "📁" },
        holdings: ungrouped,
      });
    }

    return groups;
  }, [portfolio, portfolioHoldings, customPortfolios, symbolQuery]);

  const totalFilteredCount = React.useMemo(
    () => groupedSymbols.reduce((n, g) => n + g.holdings.length, 0),
    [groupedSymbols],
  );

  // Click-outside closes symbol dropdown; Escape closes modal
  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      if (
        symbolDropdownRef.current &&
        !symbolDropdownRef.current.contains(e.target as Node) &&
        !symbolInputRef.current?.contains(e.target as Node)
      ) {
        setShowSymbolDropdown(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const handleSymbolSelect = (holding: (typeof portfolio)[0]) => {
    // Find which portfolio this holding belongs to
    const ownerPortfolioId =
      Object.entries(portfolioHoldings).find(([, holdings]) =>
        holdings.some((h) => h.id === holding.id),
      )?.[0] ?? "";

    setFormData((prev) => ({
      ...prev,
      symbol: holding.symbol,
      port_api_type: holding.port_api_type,
      sector: holding.sector || prev.sector,
      country: holding.country || prev.country,
      currency: holding.purchase_currency,
      proj_id: holding.project_id || "",
      portfolioId: ownerPortfolioId || prev.portfolioId,
    }));
    setSymbolQuery(holding.symbol);
    setSelectedHoldingShares(holding.shares);
    setShowSymbolDropdown(false);
  };

  const loadHoldingPortfolios = async (
    holdingId: number,
  ): Promise<string[]> => {
    try {
      const response = await apiFetch(`/api/holdings/${holdingId}/portfolios`);
      if (response.ok) {
        const portfolioIds = await response.json();
        return portfolioIds.map((id: number) => id.toString());
      }
    } catch (error) {
      console.error("Error loading holding portfolios:", error);
    }
    return [];
  };

  const fetchFundData = async (projId: string) => {
    if (!projId) return null;
    for (let daysBack = 0; daysBack <= 7; daysBack++) {
      try {
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() - daysBack);
        const navDate = targetDate.toISOString().split("T")[0];
        const response = await fetch(
          `https://api.sec.or.th/FundDailyInfo/${projId}/dailynav/${navDate}`,
        );
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const userSymbol = formData.symbol;
            const cleanSymbol = userSymbol.replace(/-SSF$|-RMF$|-A$/, "");
            let match = data.find(
              (f: any) =>
                f.class_abbr_name === userSymbol ||
                f.class_abbr_name === cleanSymbol,
            );
            if (!match)
              match = data.find(
                (f: any) =>
                  f.class_abbr_name?.includes(cleanSymbol) ||
                  cleanSymbol.includes(f.class_abbr_name),
              );
            return match ? [match] : data;
          }
        }
      } catch {
        /* try next day */
      }
    }
    return null;
  };

  const handleFundSelection = (projectId: string, fundName: string) => {
    setFormData((prev) => ({ ...prev, proj_id: projectId, symbol: fundName }));
    setSymbolQuery(fundName);
    setShowFundSearchModal(false);
    toast.success("Fund selected");
  };

  useEffect(() => {
    if (isOpen) {
      loadCustomPortfolios();
      loadSectors();
      loadCountries();
      loadBrokers();
      setPdfPassword(getConfigValue("pdf_password", ""));

      setSymbolQuery("");
      setShowSymbolDropdown(false);
      setSelectedHoldingShares(null);

      if (editingHolding) {
        setAction("BUY");
        setSymbolQuery(editingHolding.symbol || "");
        loadHoldingPortfolios(editingHolding.id).then((portfolioIds) => {
          setFormData({
            symbol: editingHolding.symbol || "",
            portfolioId: portfolioIds.length > 0 ? portfolioIds[0] : "",
            port_api_type: editingHolding.port_api_type || "us_stock",
            shares: editingHolding.shares?.toString() || "",
            price: editingHolding.purchase_price?.toString() || "",
            currency: editingHolding.purchase_currency || "USD",
            fee: "",
            date: editingHolding.created_at
              ? new Date(editingHolding.created_at).toISOString().split("T")[0]
              : new Date().toISOString().split("T")[0],
            notes: "",
            proj_id: editingHolding.project_id || "",
            sector: editingHolding.sector || "",
            country: editingHolding.country || "",
            broker: editingHolding.broker || "",
          });
        });

        // Best-effort fallback for holdings edited before broker lived on the
        // holding itself — doesn't block the main prefill above.
        if (!editingHolding.broker) {
          loadTradingHistory().then(() => {
            const lastBroker = useTradingHistoryStore
              .getState()
              .tradingHistory.filter(
                (r) => r.symbol === editingHolding.symbol && r.broker,
              )
              .sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              )[0]?.broker;
            if (lastBroker) {
              setFormData((prev) =>
                prev.broker ? prev : { ...prev, broker: lastBroker },
              );
            }
          });
        }
      } else {
        const initialAction = prefillData?.action || "BUY";
        setAction(initialAction);
        setSymbolQuery(prefillData?.symbol || "");
        setFormData({
          symbol: prefillData?.symbol || "",
          portfolioId: prefillData?.portfolioId || "",
          port_api_type: prefillData?.port_api_type || "us_stock",
          shares: "",
          price:
            initialAction === "SELL" && prefillData?.currentPrice
              ? String(prefillData.currentPrice)
              : "",
          currency: prefillData?.currency || "USD",
          fee: "",
          date: new Date().toISOString().split("T")[0],
          notes: "",
          proj_id: "",
          sector: prefillData?.sector || "",
          country: prefillData?.country || "",
          broker: "",
        });
      }
    }
  }, [isOpen]);

  const isLockedFromPrefill = action === "SELL" && !!prefillData?.symbol;

  // Auto-set currency and port_api_type when portfolio changes (skip when locked)
  useEffect(() => {
    if (!formData.portfolioId) return;
    if (isLockedFromPrefill) return;
    const portfolio = customPortfolios.find(
      (p) => p.id.toString() === formData.portfolioId,
    );
    if (!portfolio) return;
    const currency = portfolio.currency as "USD" | "THB";
    let port_api_type: typeof formData.port_api_type =
      currency === "USD" ? "us_stock" : "th_stock";
    if (portfolio.name.includes("กองทุน"))
      port_api_type = currency === "USD" ? "us_fund" : "th_fund";
    else if (portfolio.name.includes("ทองคำ")) port_api_type = "gold";
    setFormData((prev) => ({ ...prev, currency, port_api_type }));
  }, [formData.portfolioId, customPortfolios]);

  const parseDateDMY = (d: string) => {
    if (!d) return "";
    const parts = d.split("/");
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : d;
  };

  const handlePdfParse = async (file: File) => {
    setPdfParsing(true);
    try {
      const payload = new FormData();
      payload.append("file", file);
      if (pdfPassword) payload.append("password", pdfPassword);
      const res = await apiFetch("/api/parse-dime-pdf", {
        method: "POST",
        body: payload,
      });
      const resData = await res.json();
      const p = resData.parsed;
      if (p.action) setAction(p.action === "SELL" ? "SELL" : "BUY");
      if (p.symbol) setSymbolQuery(p.symbol);

      const matchingHolding = p.symbol
        ? portfolio.find((h) => h.symbol === p.symbol)
        : null;
      const ownerPortfolioId = matchingHolding
        ? (Object.entries(portfolioHoldings).find(([, holdings]) =>
            holdings.some((h) => h.id === matchingHolding.id)
          )?.[0] ?? "")
        : "";
      if (matchingHolding) setSelectedHoldingShares(matchingHolding.shares);

      setFormData((prev) => ({
        ...prev,
        symbol: p.symbol || prev.symbol,
        port_api_type: p.currency === "THB" ? "th_stock" : "us_stock",
        price: p.unitPrice ? String(p.unitPrice) : prev.price,
        shares: p.shares ? String(p.shares) : prev.shares,
        currency: (p.currency as "USD" | "THB") || prev.currency,
        fee: p.fees != null ? String(p.fees) : prev.fee,
        date: parseDateDMY(p.effectiveDate || p.settlementDate) || prev.date,
        sector: matchingHolding?.sector || prev.sector,
        country: matchingHolding?.country || prev.country,
        portfolioId: ownerPortfolioId || prev.portfolioId,
        broker: brokers.find((b) => b.key === "dime") ? "dime" : prev.broker,
      }));
      if (pdfInputRef.current) pdfInputRef.current.value = "";
      toast.success(
        `Prefilled: ${p.symbol} ${p.action} ${p.shares} @ ${p.unitPrice}`,
      );
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Parse PDF ไม่สำเร็จ");
    } finally {
      setPdfParsing(false);
    }
  };

  const totalAmount = () => {
    const p = parseFloat(formData.price) || 0;
    const s = parseFloat(formData.shares) || 0;
    return p * s;
  };

  const toIsoDate = (dateString: string) => `${dateString}T00:00:00.000Z`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !formData.symbol ||
      !formData.shares ||
      !formData.price ||
      !formData.portfolioId
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (isThaiFund && !formData.proj_id) {
      toast.error("Please select a fund via the search button");
      return;
    }
    setLoading(true);
    try {
      let finalSymbol = formData.symbol.toUpperCase();
      let finalPrice = parseFloat(formData.price);

      if (isThaiFund && formData.proj_id) {
        setFetchingFundData(true);
        const fundData = await fetchFundData(formData.proj_id);
        setFetchingFundData(false);
        if (fundData && fundData.length > 0) {
          const latest = fundData[0];
          if (latest.fund_name) finalSymbol = latest.fund_name;
          if (!formData.price && latest.last_val)
            finalPrice = parseFloat(latest.last_val);
          toast.success("Fund NAV loaded from SEC");
        } else {
          toast(
            `Fund ID ${formData.proj_id} not found in SEC DB — using manual entry`,
            { icon: "⚠️", duration: 4000 },
          );
        }
      }

      if (isEditMode && editingHolding) {
        const detectFundClass = (sym: string) => {
          if (sym.endsWith("-SSF")) return "SSF";
          if (sym.endsWith("-RMF")) return "RMF";
          if (sym.endsWith("-A")) return "A";
          return undefined;
        };
        await updateHolding(editingHolding.id, {
          symbol: finalSymbol,
          name: finalSymbol,
          port_api_type: formData.port_api_type,
          sector: formData.sector,
          country: formData.country,
          shares: parseFloat(formData.shares),
          purchase_price: finalPrice,
          purchase_currency: formData.currency,
          fund_class: isThaiFund ? detectFundClass(finalSymbol) : undefined,
          project_id: isThaiFund ? formData.proj_id : undefined,
          created_at: toIsoDate(formData.date),
          portfolio_id: parseInt(formData.portfolioId),
          broker: formData.broker || undefined,
        });
        const currentPortfolioIds = await loadHoldingPortfolios(
          editingHolding.id,
        );
        for (const pId of currentPortfolioIds) {
          await removeHoldingFromPortfolio(parseInt(pId), editingHolding.id);
        }
        if (formData.portfolioId) {
          await assignHoldingToPortfolio(
            parseInt(formData.portfolioId),
            editingHolding.id,
          );
        }
        await loadPortfolio();
        await loadLatestPrices();
        toast.success("Holding updated successfully");
      } else if (action === "SELL") {
        const sharesToSell = parseFloat(formData.shares);
        if (effectiveMaxShares && sharesToSell > effectiveMaxShares) {
          toast.error(`Cannot sell more than ${effectiveMaxShares} shares`);
          return;
        }
        await sellHolding(
          finalSymbol,
          sharesToSell,
          finalPrice,
              prefillData
                ? {
                    name: prefillData.name || finalSymbol,
                    port_api_type: formData.port_api_type,
                    purchase_currency: formData.currency,
                    created_at: toIsoDate(formData.date),
                    portfolio_id: parseInt(formData.portfolioId),
                    broker: formData.broker || undefined,
                  }
                : undefined,
        );
        await Promise.all([
          loadTradingHistory(true),
          loadPnLSummary(true),
          loadPortfolio(),
          loadLatestPrices(),
        ]);
      } else {
        await addTradingRecord({
          symbol: finalSymbol,
          name: finalSymbol,
          port_api_type: formData.port_api_type,
          action,
          shares: parseFloat(formData.shares),
          price: finalPrice,
          currency: formData.currency,
          total_amount: finalPrice * parseFloat(formData.shares),
          fees: formData.fee ? parseFloat(formData.fee) : undefined,
          notes: formData.notes || undefined,
          broker: formData.broker || undefined,
          created_at: toIsoDate(formData.date),
          portfolio_id: parseInt(formData.portfolioId),
        });
        await Promise.all([
          loadTradingHistory(true),
          loadPnLSummary(true),
          loadPortfolio(),
          loadLatestPrices(),
        ]);
        toast.success(`BUY transaction added`);
      }
      onClose();
    } catch {
      toast.error("Failed to add transaction");
    } finally {
      setLoading(false);
      setFetchingFundData(false);
    }
  };

  const isBuy = action === "BUY";
  const isThaiFund = formData.port_api_type === "th_fund";
  const currSymbol = formData.currency === "USD" ? "$" : "฿";
  const effectiveMaxShares =
    prefillData?.maxShares ??
    (action === "SELL" ? selectedHoldingShares : null);

  const portal = createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="sheet fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-5xl shadow-2xl"
          >
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center gap-4 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 flex-1">
                  {isEditMode ? (
                    <Edit3 className="w-5 h-5" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                  {isEditMode
                    ? `Edit ${isThaiFund ? "Fund" : "Stock"}`
                    : "Add Transaction"}
                </h2>

                {/* PDF import — inline in header (hidden in edit mode) */}
                {!isEditMode && (
                  <div className="shrink-0">
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handlePdfParse(f);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => pdfInputRef.current?.click()}
                      disabled={pdfParsing}
                      className={buttonClass('secondary', 'sm')}
                    >
                      <img
                        src="/banks/dimebykkp_logo.jpeg"
                        alt="Dime by KKP"
                        className="w-3.5 h-3.5 rounded-sm object-cover shrink-0 self-center"
                      />
                      <span className="leading-none self-center">
                        {pdfParsing ? "Reading..." : "Import from Dime"}
                      </span>
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body — 2 columns */}
              <div
                className="grid gap-0"
                style={{ gridTemplateColumns: "60% 40%" }}
              >
                {/* LEFT */}
                <div className="p-6 space-y-4">
                  {/* BUY / SELL toggle — hidden in edit mode */}
                  {!isEditMode && (
                    <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 w-full">
                      <button
                        type="button"
                        onClick={() => setAction("BUY")}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-all duration-200 ${
                          isBuy
                            ? "bg-gain-gradient text-white"
                            : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        }`}
                      >
                        <TrendingUp className="w-3 h-3" />
                        BUY
                      </button>
                      <div className="w-px bg-gray-200 dark:bg-gray-600 shrink-0" />
                      <button
                        type="button"
                        onClick={() => setAction("SELL")}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-all duration-200 ${
                          !isBuy
                            ? "bg-loss-gradient text-white"
                            : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        }`}
                      >
                        <TrendingDown className="w-3 h-3" />
                        SELL
                      </button>
                    </div>
                  )}

                  {/* Symbol — combobox */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {isThaiFund ? "Fund Symbol" : "Symbol"}{" "}
                      <span className="text-loss">*</span>
                    </label>
                    <div className="relative">
                      <input
                        ref={symbolInputRef}
                        type="text"
                        value={symbolQuery}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setSymbolQuery(val);
                          setFormData((prev) => ({ ...prev, symbol: val }));
                          setSelectedHoldingShares(null);
                          setShowSymbolDropdown(true);
                        }}
                        onFocus={() =>
                          !isThaiFund &&
                          !isLockedFromPrefill &&
                          setShowSymbolDropdown(true)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setShowSymbolDropdown(false);
                        }}
                        disabled={isThaiFund || isLockedFromPrefill}
                        placeholder={
                          isThaiFund
                            ? "Use search to select fund"
                            : "Search or enter symbol..."
                        }
                        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          isThaiFund || isLockedFromPrefill
                            ? "bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                            : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        } ${selectedHoldingShares !== null ? "pr-28" : ""}`}
                        autoComplete="off"
                        required
                      />
                      {/* shares overlay */}
                      {selectedHoldingShares !== null && (
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-400 pointer-events-none">
                          {selectedHoldingShares.toLocaleString()} shares
                        </span>
                      )}
                      {/* "New" badge when query doesn't match any holding */}
                      {!isThaiFund &&
                        !isLockedFromPrefill &&
                        symbolQuery &&
                        totalFilteredCount === 0 &&
                        selectedHoldingShares === null && (
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                            NEW
                          </span>
                        )}
                    </div>

                    {/* Dropdown */}
                    {showSymbolDropdown &&
                      !isThaiFund &&
                      !isLockedFromPrefill && (
                        <div
                          ref={symbolDropdownRef}
                          className="absolute z-[70] mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-56 overflow-y-auto"
                        >
                          {totalFilteredCount > 0 ? (
                            groupedSymbols.map((group, gi) => (
                              <div key={group.portfolio.id}>
                                {/* Portfolio header */}
                                <div
                                  className={`flex items-center gap-1.5 px-3 py-1 bg-gray-50 dark:bg-gray-700/60 ${gi > 0 ? "border-t border-gray-100 dark:border-gray-700" : ""}`}
                                >
                                  <span className="text-xs">
                                    {group.portfolio.icon}
                                  </span>
                                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    {group.portfolio.name}
                                  </span>
                                </div>
                                {/* Holdings in this portfolio */}
                                {group.holdings.map((h) => (
                                  <button
                                    key={h.id}
                                    type="button"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleSymbolSelect(h);
                                    }}
                                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                  >
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                      {h.symbol}
                                    </span>
                                    <span className="text-xs text-gray-400 dark:text-gray-400">
                                      {h.shares.toLocaleString()} shares
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              <Plus className="w-3.5 h-3.5 text-blue-500" />
                              Add new:{" "}
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {symbolQuery}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  {/* Portfolio */}
                  <div
                    className={`overflow-x-auto ${isLockedFromPrefill ? "pointer-events-none opacity-60" : ""}`}
                    style={{ scrollbarWidth: "none" }}
                  >
                    <div
                      className="flex gap-2 pb-2"
                      style={{ minWidth: "max-content" }}
                    >
                      {customPortfolios.map((p) => (
                        <label
                          key={p.id}
                          className={`relative flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-all flex-shrink-0 ${
                            formData.portfolioId === p.id.toString()
                              ? "border-primary-500 bg-brand-soft"
                              : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                          }`}
                        >
                          <input
                            type="radio"
                            name="portfolio"
                            value={p.id.toString()}
                            checked={formData.portfolioId === p.id.toString()}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                portfolioId: p.id.toString(),
                              }))
                            }
                            className="sr-only"
                          />
                          <span className="text-base leading-none">
                            {p.icon}
                          </span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white whitespace-nowrap">
                            {p.name}
                          </span>
                        </label>
                      ))}
                      <button
                        type="button"
                        onClick={() => openCustomPortfolioModal()}
                        className="relative flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer transition-all hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 flex-shrink-0"
                      >
                        <span className="text-base leading-none">➕</span>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          Add new port
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Project ID — Thai fund only */}
                  {isThaiFund && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Project ID <span className="text-loss">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.proj_id}
                          disabled
                          placeholder="Use search to select fund"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        />
                        <button
                          type="button"
                          onClick={() => setShowFundSearchModal(true)}
                          className={buttonClass('secondary', 'md')}
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>
                      {formData.proj_id && (
                        <div className="mt-1.5 flex items-center justify-between">
                          <p className="text-xs text-gain">
                            ✓ {formData.symbol}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                proj_id: "",
                                symbol: "",
                              }))
                            }
                            className="text-xs text-loss hover:text-red-600 underline"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                      {fetchingFundData && (
                        <p className="mt-1 text-xs text-blue-500">
                          Fetching fund NAV...
                        </p>
                      )}
                    </div>
                  )}

                  {/* Sector + Country + Broker */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sector
                      </label>
                      <Select
                        value={formData.sector}
                        options={sectors.map((s) => ({
                          value: s.key,
                          label: s.label,
                          icon: s.icon,
                        }))}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, sector: value }))
                        }
                        placeholder="Select sector..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <Select
                        value={formData.country}
                        options={countries.map((c) => ({
                          value: c.key,
                          label: c.label,
                          icon: c.icon,
                        }))}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, country: value }))
                        }
                        placeholder="Select country..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Broker
                      </label>
                      <Select
                        value={formData.broker}
                        options={brokers.map((b) => ({
                          value: b.key,
                          label: b.label,
                          iconNode: (
                            <BrokerLogo
                              brokerKey={b.key}
                              fallbackIcon={b.icon}
                              size={16}
                            />
                          ),
                        }))}
                        onChange={(value) =>
                          setFormData((prev) => ({ ...prev, broker: value }))
                        }
                        placeholder="Select broker..."
                      />
                    </div>
                  </div>

                  {/* Shares + Price */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Shares <span className="text-loss">*</span>
                        </label>
                        {action === "SELL" && effectiveMaxShares && (
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                shares: String(effectiveMaxShares),
                              }))
                            }
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            Sell All
                          </button>
                        )}
                      </div>
                      <input
                        type="number"
                        value={formData.shares}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            shares: e.target.value,
                          }))
                        }
                        placeholder={FORM_CONSTANTS.SHARES_PLACEHOLDER}
                        max={
                          action === "SELL" && effectiveMaxShares
                            ? effectiveMaxShares
                            : undefined
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {isEditMode ? "Purchase Price" : "Price"}{" "}
                        <span className="text-loss">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          {currSymbol}
                        </span>
                        <input
                          type="number"
                          step="0.0001"
                          value={formData.price}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              price: e.target.value,
                            }))
                          }
                          placeholder={FORM_CONSTANTS.PRICE_PLACEHOLDER}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Fee + Date */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fee (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                          {currSymbol}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.fee}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              fee: e.target.value,
                            }))
                          }
                          placeholder="0"
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date <span className="text-loss">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            date: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      rows={2}
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey)
                          e.preventDefault();
                      }}
                      placeholder=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                  </div>
                </div>

                {/* RIGHT */}
                <div className="p-6 flex flex-col gap-4">
                  {/* Receipt */}
                  <div className="flex-1 flex flex-col">
                    {/* Receipt paper */}
                    <div className="flex-1 bg-gray-50 dark:bg-gray-900/40 rounded-t-xl px-5 pt-5 pb-3 font-mono text-xs relative overflow-hidden">
                      {/* Header */}
                      <div className="text-center mb-4">
                        <p className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-400">
                          Transaction Receipt
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-gray-400 mt-0.5">
                          {formatDate(
                            formData.date ||
                              new Date().toISOString().split("T")[0],
                          )}
                        </p>
                      </div>

                      {/* Dotted separator */}
                      <div className="border-t border-dashed border-gray-300 dark:border-gray-600 mb-3" />

                      {/* Rows */}
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400 dark:text-gray-400">
                            Type
                          </span>
                          <motion.span
                            key={action}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.15 }}
                            className={`font-bold ${isEditMode ? "text-gray-700 dark:text-gray-300" : isBuy ? "text-blue-600 dark:text-blue-400" : "text-orange-500 dark:text-orange-400"}`}
                          >
                            {isEditMode ? "EDIT" : action}
                          </motion.span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400 dark:text-gray-400">
                            Symbol
                          </span>
                          <motion.span
                            key={formData.symbol}
                            initial={{ opacity: 0, x: 8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.15 }}
                            className="text-gray-800 dark:text-gray-200 font-semibold"
                          >
                            {formData.symbol || "—"}
                          </motion.span>
                        </div>
                        <AnimatePresence initial={false}>
                          {formData.portfolioId && (
                            <motion.div
                              key="portfolio-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Portfolio
                              </span>
                              <motion.span
                                key={formData.portfolioId}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-800 dark:text-gray-200"
                              >
                                {(() => {
                                  const p = customPortfolios.find(
                                    (p) => p.id.toString() === formData.portfolioId,
                                  );
                                  return p ? `${p.icon} ${p.name}` : "—";
                                })()}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.sector && (
                            <motion.div
                              key="sector-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Sector
                              </span>
                              <motion.span
                                key={formData.sector}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-800 dark:text-gray-200"
                              >
                                {sectors.find((s) => s.key === formData.sector)?.icon}{" "}
                                {sectors.find((s) => s.key === formData.sector)?.label || formData.sector}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.country && (
                            <motion.div
                              key="country-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Country
                              </span>
                              <motion.span
                                key={formData.country}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-800 dark:text-gray-200"
                              >
                                {countries.find((c) => c.key === formData.country)?.icon}{" "}
                                {countries.find((c) => c.key === formData.country)?.label || formData.country}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.broker && (
                            <motion.div
                              key="broker-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Broker
                              </span>
                              <motion.span
                                key={formData.broker}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200"
                              >
                                <BrokerLogo
                                  brokerKey={formData.broker}
                                  fallbackIcon={
                                    brokers.find((b) => b.key === formData.broker)?.icon
                                  }
                                  size={14}
                                />
                                {brokers.find((b) => b.key === formData.broker)?.label || formData.broker}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.shares && (
                            <motion.div
                              key="shares-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Shares
                              </span>
                              <motion.span
                                key={formData.shares}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-800 dark:text-gray-200"
                              >
                                {parseFloat(formData.shares).toLocaleString()}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.price && (
                            <motion.div
                              key="price-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Price
                              </span>
                              <motion.span
                                key={formData.price}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-800 dark:text-gray-200"
                              >
                                {currSymbol}
                                {parseFloat(formData.price).toLocaleString(
                                  "en-US",
                                  { minimumFractionDigits: 2 },
                                )}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.notes && (
                            <motion.div
                              key="notes-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between gap-3 overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400 shrink-0">
                                Notes
                              </span>
                              <motion.span
                                key={formData.notes}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-500 dark:text-gray-400 text-right break-all"
                              >
                                {formData.notes}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <AnimatePresence initial={false}>
                          {formData.fee && parseFloat(formData.fee) > 0 && (
                            <motion.div
                              key="fee-row"
                              initial={{ opacity: 0, height: 0, y: -6 }}
                              animate={{ opacity: 1, height: "auto", y: 0 }}
                              exit={{ opacity: 0, height: 0, y: -6 }}
                              transition={{ duration: 0.18, ease: "easeOut" }}
                              className="flex justify-between overflow-hidden"
                            >
                              <span className="text-gray-400 dark:text-gray-400">
                                Fee
                              </span>
                              <motion.span
                                key={formData.fee}
                                initial={{ opacity: 0, x: 8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.12 }}
                                className="text-gray-500 dark:text-gray-400"
                              >
                                {currSymbol}
                                {parseFloat(formData.fee).toLocaleString(
                                  "en-US",
                                  { minimumFractionDigits: 2 },
                                )}
                              </motion.span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Total */}
                      <AnimatePresence initial={false}>
                        {formData.shares && formData.price && (
                          <motion.div
                            key="total-section"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-3" />
                            <div className="flex justify-between items-baseline">
                              <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">
                                Total
                              </span>
                              <motion.span
                                key={totalAmount().toFixed(2)}
                                initial={{ scale: 1.08, opacity: 0.7 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                className="text-base font-bold text-gray-900 dark:text-white"
                              >
                                {currSymbol}
                                {totalAmount().toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                })}
                              </motion.span>
                            </div>
                            {exchangeRate > 0 && (
                              <motion.p
                                key={`fx-${totalAmount().toFixed(0)}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.15 }}
                                className="text-right text-[10px] text-gray-400 dark:text-gray-400 mt-0.5"
                              >
                                {formData.currency === "USD"
                                  ? `≈ ฿${(totalAmount() * exchangeRate).toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                                  : `≈ $${(totalAmount() / exchangeRate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                              </motion.p>
                            )}

                            {/* Est. P&L for SELL */}
                            {action === "SELL" &&
                              prefillData?.purchasePrice &&
                              (() => {
                                const shares = parseFloat(formData.shares) || 0;
                                const sellVal =
                                  (parseFloat(formData.price) || 0) * shares;
                                const costBasis =
                                  convertPrice(
                                    prefillData.purchasePrice!,
                                    prefillData.purchaseCurrency ||
                                      formData.currency,
                                    formData.currency,
                                  ) * shares;
                                const gainLoss = sellVal - costBasis;
                                return shares > 0 ? (
                                  <AnimatePresence initial={false}>
                                    <motion.div
                                      key="pnl-section"
                                      initial={{ opacity: 0, y: 6 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 6 }}
                                      transition={{ duration: 0.18 }}
                                    >
                                      <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-3" />
                                      <div className="flex justify-between items-baseline">
                                        <span className="text-gray-400 dark:text-gray-400">
                                          Est. P&L
                                        </span>
                                        <motion.span
                                          key={gainLoss.toFixed(2)}
                                          initial={{ scale: 1.08, opacity: 0.7 }}
                                          animate={{ scale: 1, opacity: 1 }}
                                          transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                          className={`font-bold text-sm ${gainLoss >= 0 ? "text-gain" : "text-loss"}`}
                                        >
                                          {gainLoss >= 0 ? "+" : ""}
                                          {currSymbol}
                                          {gainLoss.toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </motion.span>
                                      </div>
                                    </motion.div>
                                  </AnimatePresence>
                                ) : null;
                              })()}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Zigzag tear bottom */}
                    <div
                      className="h-3 bg-gray-50 dark:bg-gray-900/40"
                      style={{
                        maskImage:
                          "radial-gradient(circle at 8px 100%, transparent 8px, black 8px)",
                        maskSize: "16px 16px",
                        maskPosition: "0 0",
                        WebkitMaskImage:
                          "radial-gradient(circle at 8px 100%, transparent 8px, black 8px)",
                        WebkitMaskSize: "16px 16px",
                        WebkitMaskPosition: "0 0",
                      }}
                    />
                  </div>

                  {/* Sell-all warning */}
                  {action === "SELL" &&
                    effectiveMaxShares &&
                    formData.shares === String(effectiveMaxShares) && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Selling entire position. This stock will be removed
                          from your portfolio.
                        </p>
                      </div>
                    )}

                  {/* Submit */}
                  <Button className="w-full"
                    type="submit"
                    loading={loading || fetchingFundData}
                    disabled={
                      !formData.symbol ||
                      !formData.shares ||
                      !formData.price ||
                      !formData.portfolioId ||
                      (isThaiFund && !formData.proj_id)
                    }
                  >
                    {isEditMode ? (
                      <Edit3 className="w-4 h-4" />
                    ) : isBuy ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    {fetchingFundData
                      ? "Fetching NAV..."
                      : isEditMode
                        ? `Update ${formData.symbol || (isThaiFund ? "Fund" : "Stock")}`
                        : `${action} ${formData.symbol || (isThaiFund ? "Fund" : "Stock")}`}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );

  return (
    <>
      {portal}
      <FundSearchModal
        isOpen={showFundSearchModal}
        onClose={() => setShowFundSearchModal(false)}
        onSelectFund={handleFundSelection}
      />
    </>
  );
};

export default AddTransactionModal;
